import { inject, Injectable } from '@angular/core';
import { StrapiDataMedia } from '@core/const';
import {
  PLATFORM_FILTER_PARAMETER,
  StrapiBaseUrlService,
  StrapiCollectionEntityByCityApiService,
} from '@core/data-access';
import { StrapiMediaNormalizer } from '@core/utils';
import {
  BannerDefault,
  BannerDefaultComponentStrapi,
  BannerWithTab,
  CategoryCarousel,
  ImageBanner,
  PromoEntityStrapi,
  ServiceItemCarousel,
} from '@promo/const';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PromoApiService extends StrapiCollectionEntityByCityApiService<PromoEntityStrapi> {
  private strapiMediaNormalizer: StrapiMediaNormalizer = inject(StrapiMediaNormalizer);

  private readonly strapiBaseUrl = inject(StrapiBaseUrlService).baseUrl;

  constructor() {
    super('promos', 'citiesIncluded', 'citiesExcluded');
  }

  getPromoData(baseStoreCode: string): Observable<PromoEntityStrapi | null> {
    // TODO: Исправить any в StrapiPopulate. Стоит ограничить вложенность через параметр в generic
    const populateBanners = {
      image: {
        populate: '*',
      },
    };

    // TODO: Разрастается лапша и некрасиво выглядит, подумать как улучшить.
    // Думаю насчет того, чтобы создать функцию с ...args  и рекурсивно создавать объект.
    const populate = {
      serviceCarousel: {
        populate: {
          items: {
            populate: populateBanners,
            filters: PLATFORM_FILTER_PARAMETER,
          },
        },
      },
      bannerOneRows: {
        populate: populateBanners,
        filters: PLATFORM_FILTER_PARAMETER,
      },
      categoryCarousel: {
        populate: populateBanners,
        filters: PLATFORM_FILTER_PARAMETER,
      },
      bannersGrid: {
        populate: populateBanners,
        filters: PLATFORM_FILTER_PARAMETER,
      },
      bannersWithButton: {
        populate: {
          banners: {
            populate: populateBanners,
            filters: PLATFORM_FILTER_PARAMETER,
          },
        },
      },
      bannerNumberShop: {
        populate: populateBanners,
      },
      bannersWithTab: {
        populate: {
          bannerWithTabGroups: {
            populate: {
              banners: {
                populate: populateBanners,
                filters: PLATFORM_FILTER_PARAMETER,
              },
            },
          },
        },
      },
      bannerSearch: {
        populate: populateBanners,
      },
      video: {
        populate: '*',
      },
      infoblocks: {
        populate: '*',
      },
      banner: {
        populate: populateBanners,
      },
    } as any;

    return this.getListByCity(
      {
        pagination: { page: 1, pageSize: 1 },
        populate,
      },
      baseStoreCode,
    ).pipe(
      map((promos) => (promos.length ? promos[0] : null)),
      map((promo) => {
        if (!promo) return promo;

        return {
          serviceCarousel: promo.serviceCarousel
            ? {
                ...promo.serviceCarousel,
                items: this.getBannersData(promo.serviceCarousel.items) as ServiceItemCarousel[],
              }
            : undefined,
          categoryCarousel: this.getBannersData(promo.categoryCarousel) as CategoryCarousel[],
          bannerOneRows: this.getBannersData(promo.bannerOneRows),
          bannersGrid: this.getBannersData(promo.bannersGrid),
          bannersWithButton: {
            label: promo.bannersWithButton?.label || '',
            banners: this.getBannersData(promo.bannersWithButton?.banners),
          },
          bannerSearch: promo.bannerSearch?.image
            ? {
                ...promo.bannerSearch,
                url: this.getBannerImageUrl(promo.bannerSearch.image),
              }
            : undefined,
          bannerNumberShop: promo.bannerNumberShop
            ? {
                ...promo.bannerNumberShop,
                ...this.getBannerData(promo.bannerNumberShop),
              }
            : undefined,
          bannersWithTab: promo.bannersWithTab
            ? {
                ...promo.bannersWithTab,
                bannerWithTabGroups: promo.bannersWithTab.bannerWithTabGroups.map((bannerGroup) => ({
                  ...bannerGroup,
                  banners: this.getBannersData(bannerGroup.banners) as BannerWithTab[],
                })),
              }
            : undefined,
          video: promo.video,
          infoblocks: promo.infoblocks,
          banner: promo.banner?.image
            ? {
                ...promo.banner,
                url: this.getBannerImageUrl(promo.banner.image),
              }
            : undefined,
        };
      }),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  private getBannerData(banner: BannerDefaultComponentStrapi): BannerDefault | undefined {
    return banner.image
      ? {
          ...banner,
          url: this.getBannerImageUrl(banner.image),
        }
      : undefined;
  }

  private getBannersData(banners: BannerDefaultComponentStrapi[] | undefined): BannerDefault[] {
    if (!banners) return [];

    return banners.map((banner) => this.getBannerData(banner)).filter((banner) => !!banner) || [];
  }

  private getImageUrl(images: StrapiDataMedia[]): string {
    const image = images?.[0];

    if (image && this.strapiBaseUrl) {
      return this.strapiMediaNormalizer.convert(image, this.strapiBaseUrl).url;
    }

    return image?.url || '';
  }

  private getBannerImageUrl(imageBanner: ImageBanner): BannerDefault['url'] {
    const { imageMobile, imageTablet, imageDesktop } = imageBanner;

    return {
      isMobile: this.getImageUrl(imageMobile),
      isTablet: this.getImageUrl(imageTablet),
      isDesktop: this.getImageUrl(imageDesktop),
    };
  }
}
